import React, { useEffect, useState } from "react";
import { formatDecimal } from "../../../utils/formatNumber";
import { useGame } from "../../useShell";
import { useDevSettings } from "./devSettings";

/** How often the readout re-reads the engine's rolling average, in ms. */
const REFRESH_MS = 250;

/** A black rim on every side, so the white reads over any part of the shop. */
const OUTLINE = [
  "1px 1px 0 #000",
  "-1px 1px 0 #000",
  "1px -1px 0 #000",
  "-1px -1px 0 #000",
].join(", ");

/**
 * The frame-rate readout: bare text in the very top-left corner,
 * switched on from the dev menu. It reads the engine's rolling average
 * (`Game.getScreenFps`) a few times a second rather than every frame —
 * a number that changes sixty times a second is unreadable, and
 * re-rendering React per frame would cost the frames it's measuring.
 * Dev builds only, and outside the paperwork design system on purpose,
 * like the dev menu it belongs to.
 */
export const FpsReadout: React.FC = () => {
  if (process.env.NODE_ENV === "production") return null;
  return <FpsReadoutGate />;
};

const FpsReadoutGate: React.FC = () => {
  const { showFps } = useDevSettings();
  if (!showFps) return null;
  return <FpsText />;
};

const FpsText: React.FC = () => {
  const game = useGame();
  const [fps, setFps] = useState(() => game.getScreenFps());

  useEffect(() => {
    const interval = window.setInterval(
      () => setFps(game.getScreenFps()),
      REFRESH_MS,
    );
    return () => window.clearInterval(interval);
  }, [game]);

  const frameMs = 1000 / Math.max(fps, 1);

  return (
    <div
      data-testid="fps-readout"
      className="pointer-events-none absolute left-0.5 top-0 z-[70] font-mono text-[10px] leading-tight tabular-nums text-white"
      style={{ textShadow: OUTLINE }}
    >
      {fps} fps · {formatDecimal(frameMs)} ms
    </div>
  );
};
